/* eslint-disable max-lines */
/* TODO: remove the above line and reduce the line count */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Button,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useDispatch, useSelector } from 'react-redux';

import Actions from './Actions';
import DownloadDropdown from './DownloadDropdown';
import { getIconComponent } from './getIcon';
import { insertPanelButton } from './insertPanelButton';
import { consultationCreation } from 'FRONTEND/actions/upload';
import ControlledCheckbox from 'FRONTEND/components/ControlledCheckbox';
import FilterDialog from 'FRONTEND/components/FilterDialog';
import { goBackToForm } from 'FRONTEND/reducers/calendarSlice';
import { displayTransition } from 'FRONTEND/reducers/consultationViewSlice';
import { selectAllRepeatedInstances } from 'FRONTEND/reducers/formSlice';
import {
  abortCreation,
  abortEdition,
  createOnePatient,
  selectPatientById,
  updateOnePatient,
} from 'FRONTEND/reducers/patientSlice';
import * as localSettings from 'FRONTEND/utils/backendApi/settingsApi';
import { addRepeatedInstancesToForm } from 'FRONTEND/utils/repeatableFields';
import { PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_SELECTOR, SCREEN_XS_PX } from 'LEGACY/constants';
import { getMSFCaseStatus, validateMSFCaseStatus } from 'LEGACY/consultations';
import { createDataTables } from 'LEGACY/datatables';
import { addUppyFieldsToForm } from 'LEGACY/filesUpload';
import { scrollToFirstFormInvalidElement, validateRequiredCheckboxes } from 'LEGACY/forms';
import { checkUniquePatient } from 'LEGACY/patients';

const GenericActionBar = ({
  title,
  back,
  download,
  actions = [],
  transitions = [],
  commentsOnly,
  workflowId = '',
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.id);
  const locale = useSelector((state) => state.user.locale);
  const patientLogicalId = useSelector((state) => selectPatientById(state, state.booking.selectedPatientId)?.logicalId);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const displayedTransition = useSelector((state) => state.consultationView.displayedTransition);

  const screenXS = useMediaQuery(`(max-width:${SCREEN_XS_PX})`);

  const hydrateEventIcons = () => {
    document.querySelectorAll('.event-icon').forEach(node => {
      if (node.innerHTML) {
        // already hydrated
        return;
      }

      const iconKey = node.getAttribute('data-icon');
      if (!iconKey) {
        return console.error('missing icon attribute');
      }

      const component = getIconComponent(iconKey);
      if (!component) {
        return console.error('missing icon', iconKey);
      }

      // Use React 18's createRoot to render the icon into the container
      const root = createRoot(node);
      root.render(component);
    });
  };

  const scrollToTransition = () => {
    if (displayedTransition) {
      insertPanelButton();
      document.querySelector(PJAX_TRANSITION_CONTAINER_SELECTOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      hydrateEventIcons();
    }
  };

  useEffect(() => {
    hydrateEventIcons();
    $(document).on('pjax:end', scrollToTransition);
    return () => {
      $(document).off('pjax:end', scrollToTransition);
    };
  }, [displayedTransition]);

  const setEventsVisibility = (showCommentsOnly) => {
    document.querySelectorAll('[data-event-comments-only]').forEach(eventElem => {
      if (showCommentsOnly && eventElem.dataset.eventCommentsOnly) {
        eventElem.classList.add('hidden');
      } else {
        eventElem.classList.remove('hidden');
      }
    });
  };

  const toggleCommentsOnly = (checked) => {
    localSettings.postSettings({
      expertId: userId,
      settings: {
        consultations_comments_only: checked,
      },
    }).then();
    setEventsVisibility(checked);
  };

  const restoreButtons = () => {
    setButtonsDisabled(false);
  };

  const handleTransitionClick = (event, id, url, noExam) => {
    // avoid visual weirdness while select2 is unloading during upcoming transition load (e.g. comment/add participant)
    const transitionContainer = document.querySelector(PJAX_TRANSITION_CONTAINER_SELECTOR);
    if (transitionContainer) {
      transitionContainer.innerHTML = '';
    }
    if (noExam === 'True') {
      // Create a "fake" form to submit the CSRF token.
      const form = document.createElement('form');
      form.setAttribute('method', 'post');
      form.setAttribute('action', url);
      form.setAttribute('data-pjax', '');
      const csrfToken = document.createElement('input');
      csrfToken.setAttribute('name', 'csrf_token');
      csrfToken.setAttribute('value', window.CSRFToken);
      form.appendChild(csrfToken);
      document.getElementById(PJAX_MAIN_CONTAINER_ID).appendChild(form);
      $(form).trigger('submit');
    } else {
      dispatch(displayTransition({ transition: id }));
      if (transitionContainer) {
        $.pjax({ url, container: PJAX_TRANSITION_CONTAINER_SELECTOR, push: false });
      }
    }
  };

  useEffect(() => {
    // Specialists.
    createDataTables();

    // The comment form is preloaded on the consultation history tab.
    const currentAction = document.querySelector('form#consultations-comment');
    if (currentAction) {
      insertPanelButton();
      dispatch(displayTransition({ transition: ('comment') }));
    }

    if (commentsOnly) {
      setEventsVisibility(commentsOnly.checked === 'True');
    }

    $(document).on('pjax:error pjax:timeout', restoreButtons);
    return () => {
      $(document).off('pjax:error pjax:timeout', restoreButtons);
    };
  }, []);

  // Manage patient creation and edition expectations (launched from the appointment form).
  const isExpectingCreation = useSelector((state) => state.patient.isExpectingCreation);
  const isExpectingEdition = useSelector((state) => state.patient.isExpectingEdition);
  const resetAppointmentFormExpectations = () => {
    if (isExpectingCreation) {
      dispatch(abortCreation());
    }
    if (isExpectingEdition) {
      dispatch(abortEdition());
    }
  };
  useEffect(() => {
    $(document).on('pjax:start', resetAppointmentFormExpectations);
    return () => {
      $(document).off('pjax:start', resetAppointmentFormExpectations);
    };
  }, [isExpectingCreation, isExpectingEdition]);


  const handleControlledCancelClick = () => {
    dispatch(goBackToForm());
  };

  const validateForm = async(form) => {
    validateRequiredCheckboxes(form);

    if (form.id === 'patients-create' || form.id === 'patients-update') {
      await checkUniquePatient();
    }

    // Manage MSF special forms.
    const hasNoValidationErrors = validateMSFCaseStatus() && form.reportValidity();
    if (!hasNoValidationErrors) {
      scrollToFirstFormInvalidElement();
    }

    return hasNoValidationErrors;
  };

  const repeatedInstances = useSelector(selectAllRepeatedInstances);

  const handleActionClick = async(event) => {
    if (!event.currentTarget.hasAttribute('form')) {
      return false;
    }

    const button = event.currentTarget;
    const form = button.form;

    // MSF close triggers a confirmation modal first.
    // We have to look for id here, as 'close' is an action and action buttons are created programmatically.
    const MSFCloseModal = $('#msf_case-close-modal');
    if (form.id === 'consultations-close' && MSFCloseModal.length) {
      MSFCloseModal.modal();
      return false;
    }

    // Remove required attributes when submitting a draft.
    const inputsRequired = document.querySelectorAll('input[required], select[required], textarea[required]');
    if (button.dataset.draft) {
      inputsRequired.forEach((input) => {
        input.required = false;
      });
    }

    const formValidated = await validateForm(form);

    // Reinstate required in case validation failed or submit fails.
    inputsRequired.forEach((input) => {
      input.required = true;
    });

    if (form.id === 'patients-create' && isExpectingCreation && formValidated) {
      dispatch(createOnePatient({ patientFormData: new FormData(form) }));
      return;
    }

    if (form.id === 'patients-update' && isExpectingEdition && formValidated) {
      dispatch(updateOnePatient({ patientFormData: new FormData(form), logicalId: patientLogicalId }));
      return;
    }
    if (formValidated) {
      setButtonsDisabled(true);
      // Manage MSF special forms.
      getMSFCaseStatus(form);
      // Manage React components
      addUppyFieldsToForm(form);
      // Add hidden inputs for all repeated instances stored in formSlice.
      addRepeatedInstancesToForm({ form, instances: repeatedInstances });
      if (form.classList.contains('consultations-draft')) {
        dispatch(consultationCreation({
          data: new FormData(form),
          isDraft: button.dataset.draft,
          redirectUrl: form.dataset.redirectDuringUpload,
          submitUrl: form.getAttribute('action'),
          workflowId,
        }));
      } else {
        $(form).trigger('submit');
      }
    }
  };

  const renderCommentsOnlyFilter = () => {
    if (!commentsOnly) {
      return;
    }

    if (screenXS) {
      return (
        <Stack direction='row' justifyContent='end' flexGrow='1'>
          <FilterDialog commentsOnly={commentsOnly} commentsFunction={toggleCommentsOnly} />
        </Stack>
      );
    }

    return (
      <Stack direction='row' justifyContent='end' flexGrow='1'>
        <FormControlLabel
          control={
            <ControlledCheckbox
              defaultChecked={commentsOnly.checked === 'True'}
              onChange={(event) => {
                toggleCommentsOnly(event.target.checked);
              }}
            />
          }
          label={commentsOnly.label}
        />
      </Stack>
    );
  };

  return (
    <div className='actionBar'>
      {back && (
        <Tooltip title={back.label}>
          <Button
            component='button'
            type='button'
            href={back.href}
            data-pjax
            className='actionBar-backButton'
            variant='contained'
            color='inherit'
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      )}

      {download && (
        <div className='actionBar-downloads'>
          <DownloadDropdown
            buttons={download.items}
            label={download.label}
          />
        </div>
      )}
      <Actions
        screenXS={screenXS}
        isExpectingCreation={isExpectingCreation}
        actions={actions}
        transitions={transitions}
        isExpectingEdition={isExpectingEdition}
        patientLogicalId={patientLogicalId}
        locale={locale}
        buttonsDisabled={buttonsDisabled}
        onControlledCancelClick={handleControlledCancelClick}
        onActionClick={handleActionClick}
        onTransitionClick={handleTransitionClick}
      />

      {renderCommentsOnlyFilter()}

      {title && (
        <Typography className='actionBar-title' variant='h2'>{typeof title === 'string' ? title : title[locale]}</Typography>
      )}
    </div>
  );
};

export default GenericActionBar;
